import { defineStore } from "pinia";
import { HoodApi } from "@/services/HoodApi";

interface NewVote {
  title: string;
  description: string;
  vote_type: string;
  required_response: number;
  response_deadline: string;
  anonymous: boolean;
  status: string;
}

interface VoteStoreState {
  votes: { [key: string]: Vote };
  ballots: { [key: string]: Ballot };
  total_homes: number;
  loading: boolean;
  error: string | null;
}

export const useVoteStore = defineStore({
  id: "votes",
  state: (): VoteStoreState => ({
    votes: {},
    ballots: {},
    total_homes: 166,
    loading: false,
    error: null,
  }),
  getters: {
    active_votes: (state) => {
      const data = Object.values(state.votes);
      return data.filter((ele) => ele.status === "active");
    },
    did_i_vote: (state) => (user_id: number, vote_id: number) => {
      const vote = state.votes[vote_id];
      if (!vote || !vote.ballots) return [];
      return vote.ballots.filter((ele) => ele.user === user_id);
    },
    all_votes: (state) => {
      return state.votes;
    },
  },
  actions: {
    async cast_ballot(ballot_obj: BallotObj): Promise<string> {
      try {
        const ballot = await HoodApi.cast_ballot(ballot_obj);
        // Refresh votes to get updated ballot counts
        await this.get_votes();
        return "success";
      } catch (e) {
        console.log(e);
        throw e;
      }
    },
    async get_votes(): Promise<void> {
      this.loading = true;
      this.error = null;
      try {
        const response = await HoodApi.get_votes();
        const mapped = response.map((item) => ({ [item.id]: item }));
        const newObj = Object.assign({}, ...mapped);
        this.votes = newObj;
      } catch (e) {
        this.error = "Failed to fetch votes";
        console.log(e);
      } finally {
        this.loading = false;
      }
    },
    async create_vote(vote: NewVote): Promise<Vote> {
      this.loading = true;
      this.error = null;
      try {
        const newVote = await HoodApi.create_a_vote(vote);
        // Add to local state
        this.votes[newVote.id] = newVote;
        return newVote;
      } catch (e: any) {
        this.error = e?.response?.data?.error || "Failed to create vote";
        throw e;
      } finally {
        this.loading = false;
      }
    },
  },
});
